import {
  Tooltip as TooltipRoot,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ohif/ui-next';
import classnames from 'classnames';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * OHIF 3.11 deleted `@ohif/ui`'s `Tooltip`. Its replacement, `@ohif/ui-next`'s
 * `Tooltip`, is a Radix compound component (`Tooltip`/`TooltipTrigger`/
 * `TooltipContent`) that ignores the old `content`/`position` props — swapping
 * the import alone makes every tooltip silently disappear, with no build error.
 *
 * This keeps the old call-site API (the subset Gleamer actually uses: `content`,
 * `position`, `className`) and renders it with the ui-next primitives, so the
 * tooltips keep working and pick up the ui-next styling.
 *
 * Note `className` lands on the wrapper element, not the tooltip box — several
 * call sites (e.g. `SidePanel`) rely on it for their own layout.
 */

type TooltipPosition =
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right'
  | 'left'
  | 'right'
  | 'top';

type TooltipProps = {
  content?: ReactNode;
  secondaryContent?: ReactNode;
  position?: TooltipPosition;
  className?: string;
  tooltipBoxClassName?: string;
  isDisabled?: boolean;
  children: ReactNode;
};

// The old component took a single `position`; Radix splits that into
// `side` + `align`.
const SIDE_AND_ALIGN: Record<
  TooltipPosition,
  { side: 'top' | 'right' | 'bottom' | 'left'; align: 'start' | 'center' | 'end' }
> = {
  bottom: { side: 'bottom', align: 'center' },
  'bottom-left': { side: 'bottom', align: 'start' },
  'bottom-right': { side: 'bottom', align: 'end' },
  top: { side: 'top', align: 'center' },
  left: { side: 'left', align: 'center' },
  right: { side: 'right', align: 'center' },
};

export function Tooltip({
  content,
  secondaryContent = null,
  position = 'bottom',
  className,
  tooltipBoxClassName,
  isDisabled = false,
  children,
}: TooltipProps) {
  const { t } = useTranslation('Buttons');
  const { side, align } = SIDE_AND_ALIGN[position] ?? SIDE_AND_ALIGN.bottom;

  const wrapper = (
    <div className={classnames('relative', className)}>{children}</div>
  );

  if (isDisabled || !content) {
    return wrapper;
  }

  return (
    // Radix throws "`Tooltip` must be used within `TooltipProvider`" without an
    // ancestor provider. The app has one (Gdata.tsx), but anything rendering this
    // outside that tree — unit tests, Storybook — would crash. Providers nest
    // harmlessly and we don't customise delays, so carry our own and stay
    // self-contained.
    <TooltipProvider>
      <TooltipRoot>
        <TooltipTrigger asChild>{wrapper}</TooltipTrigger>
        <TooltipContent
          side={side}
          align={align}
          className={tooltipBoxClassName}
        >
          <div>{typeof content === 'string' ? t(content) : content}</div>
          {secondaryContent && (
            <div className="text-aqua-pale">
              {typeof secondaryContent === 'string'
                ? t(secondaryContent)
                : secondaryContent}
            </div>
          )}
        </TooltipContent>
      </TooltipRoot>
    </TooltipProvider>
  );
}

export default Tooltip;
